import React, { useState, useEffect, useRef } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Plus, GripVertical, Pencil, Trash2, Eye, FileText, Copy, Lock, Shield, CheckCircle, AlertTriangle, Play, RotateCcw, Bug, ArrowRight, Info, AlertOctagon, Users, Globe, Flame, GitMerge, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { normalizeAcronyms } from '../utils/normalizeAcronyms';

import { RequestTypeFieldEditor } from './RequestTypeFieldEditor';
import { RequestTypeFieldCard } from './RequestTypeFieldCard';
import { describeCondition, fromSavedField, toSavedField, type FormField } from '../utils/requestTypeFields';
import { SUBMITTER_ACCESS_OPTIONS, parseNameList, validateAccess, workTypeFromServiceProjectId, workTypeOptions, type SpaceWithTeams, type SubmitterAccess } from '../utils/requestTypeAccess';
import { addTeam, listCompanyTeams } from '../utils/requestTypeTeamsApi';

interface FormMetadata {
  title: string;
  description?: string;
  assignmentGroup?: string;
  assignmentLogic?: string;
  approvalNeeded: boolean;
  approvalLogic?: string;
  portalGroup?: string;
  clientRequirementsNotes?: string;
  internalConsultantNotes?: string;
  // Phase 6.5: Access Control
  submitterAccess: SubmitterAccess;
  viewerAccess: 'requester_only' | 'organization' | 'groups' | 'agents_only';
  allowedTeams: string[];
  allowedGroups?: string; 
  allowedDepartment?: string;
  allowedIndividuals?: string;
  allowedOrgs?: string; 
  externalAccessAllowed: boolean;
  /** The team that owns this request type (the forms list groups by it). */
  team?: string;
  /** The work type this request type maps to. */
  workTypeKey?: string;
  workTypeName?: string;
  // Incident specific
  allowMajorIncident?: boolean;
  // Change specific
  changeMode?: string;
}

interface RequestTypeFormBuilderProps {
  serviceProjectId: string;
  requestType?: any | null;
  companyId: string;
  sowId?: string;
  accessToken: string;
  userRole?: 'client' | 'consultant' | 'admin';
  onSave: (data: any) => Promise<void>;
  onSaveAndAddAnother?: (data: any) => Promise<void>;
  onCancel: () => void;
  onDraftSaved?: (draftId: string) => void;
  inheritedConfig?: {
    workflowConfig: {
      workflowTemplateId: string;
      changeTypes?: string[];
    };
    sla: {
      response: Record<string, number>;
      resolution: Record<string, number>;
    };
    priorityMatrix: {
      impact: string[];
      urgency: string[];
      matrix: Record<string, string>;
    };
    riskModel?: string;
    approvals?: {
        normal: string;
        emergency: string;
    };
    blackouts?: boolean;
  };
}

export function RequestTypeFormBuilder({
  serviceProjectId,
  requestType,
  companyId,
  sowId,
  accessToken,
  userRole = 'client',
  onSave,
  onSaveAndAddAnother,
  onCancel,
  onDraftSaved,
  inheritedConfig
}: RequestTypeFormBuilderProps) {
  const [step, setStep] = useState<1 | 2>(1);
  const [metadata, setMetadata] = useState<FormMetadata>({
    title: '',
    approvalNeeded: false,
    submitterAccess: 'everyone',
    viewerAccess: 'requester_only',
    allowedTeams: [],
    externalAccessAllowed: false,
    allowMajorIncident: false,
    changeMode: ''
  });
  const [fields, setFields] = useState<FormField[]>([]);
  // Teams come from the company's spaces; a team can be added inline (RESTORE_PLAN R1.3).
  const [teams, setTeams] = useState<string[]>([]);
  const [spaces, setSpaces] = useState<SpaceWithTeams[]>([]);
  const [newTeamName, setNewTeamName] = useState('');
  const [addingTeam, setAddingTeam] = useState(false);
  const [engagementWorkTypes, setEngagementWorkTypes] = useState<Record<string, any> | null>(null);
  const [editingField, setEditingField] = useState<FormField | null>(null);
  const [showFieldDialog, setShowFieldDialog] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  
  // Lifecycle State
  const [status, setStatus] = useState<string>(requestType?.status || 'draft');
  const [isLocked, setIsLocked] = useState<boolean>(requestType?.locked || false);
  
  // Detect Context
  const isIncident = inheritedConfig?.workflowConfig?.workflowTemplateId === 'incident-management';
  const isChange = inheritedConfig?.workflowConfig?.workflowTemplateId === 'change-management';

  // Dialog States
  const [showReadyForBuildDialog, setShowReadyForBuildDialog] = useState(false);
  const [showChangeRequestDialog, setShowChangeRequestDialog] = useState(false);
  const [showUnlockDialog, setShowUnlockDialog] = useState(false);

  useEffect(() => {
    if (requestType) {
      setMetadata({
        title: requestType.requestTypeName || '',
        description: requestType.description,
        assignmentGroup: requestType.assignmentGroup,
        assignmentLogic: requestType.assignmentLogic,
        approvalNeeded: requestType.approvalRequired || false,
        approvalLogic: requestType.approvalLogic,
        portalGroup: requestType.portalGroup,
        clientRequirementsNotes: requestType.clientRequirementsNotes || requestType.notes,
        internalConsultantNotes: requestType.internalConsultantNotes,
        submitterAccess: requestType.submitterAccess || 'everyone',
        viewerAccess: requestType.viewerAccess || 'requester_only',
        allowedTeams: requestType.allowedTeams || [],
        allowedGroups: requestType.allowedGroups ? requestType.allowedGroups.join(', ') : '',
        allowedDepartment: requestType.allowedDepartment || '',
        allowedIndividuals: requestType.allowedIndividuals ? requestType.allowedIndividuals.join('\n') : '',
        allowedOrgs: requestType.allowedOrgs ? requestType.allowedOrgs.join(', ') : '',
        externalAccessAllowed: requestType.externalAccessAllowed || false,
        team: requestType.team || '',
        workTypeKey: requestType.workTypeKey || workTypeFromServiceProjectId(serviceProjectId)?.id || '',
        workTypeName: requestType.workTypeName || workTypeFromServiceProjectId(serviceProjectId)?.name || '',
        allowMajorIncident: requestType.allowMajorIncident || false,
        changeMode: requestType.changeMode || ''
      });
      setFields((requestType.fields || []).map((f: any, i: number) => fromSavedField(f, i)));
      setStatus(requestType.status || 'draft');
      setIsLocked(requestType.locked || false);
      if (requestType.id) setDraftId(requestType.id);
    } else {
      const knownWorkType = workTypeFromServiceProjectId(serviceProjectId);
      setMetadata({ 
          title: '', 
          approvalNeeded: false,
          submitterAccess: 'everyone',
          viewerAccess: 'requester_only',
          allowedTeams: [],
          externalAccessAllowed: false,
          allowMajorIncident: false,
          changeMode: '',
          workTypeKey: knownWorkType?.id || '',
          workTypeName: knownWorkType?.name || ''
      });
      // Inject mandatory fields for incidents if new
      if (isIncident) {
          setFields([
              { id: 'impact', name: 'Impact', type: 'dropdown', required: true, options: ['Critical', 'High', 'Medium', 'Low'], order: 0, isSystem: true, helpText: 'Measure of the effect on business processes' },
              { id: 'urgency', name: 'Urgency', type: 'dropdown', required: true, options: ['Critical', 'High', 'Medium', 'Low'], order: 1, isSystem: true, helpText: 'Measure of how quickly a resolution is required' },
              { id: 'affected-service', name: 'Affected Service', type: 'asset', required: true, order: 2, isSystem: true, helpText: 'The service or system experiencing the issue' },
              { id: 'description', name: 'Description', type: 'paragraph', required: true, order: 3, isSystem: true, helpText: 'Detailed description of the incident' }
          ]);
      } else {
          setFields([]);
      }
      setStep(1);
      setStatus('draft');
      setIsLocked(false);
    }
  }, [requestType, isIncident]);

  useEffect(() => {
    if (!companyId || !accessToken) return;
    let cancelled = false;
    listCompanyTeams(companyId, accessToken)
      .then(({ teams: t, spaces: sp }) => { if (!cancelled) { setTeams(t); setSpaces(sp); } })
      .catch((e) => console.warn('[RequestTypeFormBuilder] teams not loaded:', e?.message || e));
    if (sowId) {
      fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/engagement-config/${companyId}/${sowId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => { if (!cancelled && d?.engagementConfig?.workTypes) setEngagementWorkTypes(d.engagementConfig.workTypes); })
        .catch(() => {});
    }
    return () => { cancelled = true; };
  }, [companyId, accessToken, sowId]);

  const allTeams = parseNameList([...teams, ...metadata.allowedTeams, metadata.team || '']);
  const workTypeChoices = workTypeOptions(engagementWorkTypes);

  const handleAddTeam = async (assignTo: 'allowed' | 'owner') => {
    const name = newTeamName.trim();
    if (!name) return;
    setAddingTeam(true);
    try {
      const result = await addTeam(companyId, name, accessToken, spaces);
      setTeams((prev) => parseNameList([...prev, result.name]));
      if (assignTo === 'allowed') {
        setMetadata((prev) => ({ ...prev, allowedTeams: parseNameList([...prev.allowedTeams, result.name]) }));
      } else {
        setMetadata((prev) => ({ ...prev, team: result.name }));
      }
      setNewTeamName('');
      toast.success(result.savedToSpace ? `Team "${result.name}" added to ${result.savedToSpace.name || 'the space'}` : `Team "${result.name}" added to this request type (no space to hold it yet)`);
    } catch (e: any) {
      toast.error(e?.message || 'Could not add the team');
    } finally {
      setAddingTeam(false);
    }
  };

  const toggleAllowedTeam = (team: string) => {
    if (isLocked) return;
    setMetadata((prev) => ({
      ...prev,
      allowedTeams: prev.allowedTeams.includes(team) ? prev.allowedTeams.filter((t) => t !== team) : [...prev.allowedTeams, team],
    }));
  };

  const accessErrors = validateAccess({
    submitterAccess: metadata.submitterAccess,
    allowedTeams: metadata.allowedTeams,
    allowedGroups: parseNameList(metadata.allowedGroups),
    allowedDepartment: metadata.allowedDepartment,
    allowedIndividuals: parseNameList(metadata.allowedIndividuals),
    allowedOrgs: parseNameList(metadata.allowedOrgs),
  });

  const handleMetadataChange = (field: keyof FormMetadata, value: any) => {
    if (isLocked && field !== 'internalConsultantNotes') return; 
    setMetadata(prev => ({ ...prev, [field]: value }));
  };

  const canProceedToStep2 = () => {
    if (!metadata.title) return false;
    if (metadata.assignmentGroup && !metadata.assignmentLogic) return false;
    if (metadata.approvalNeeded && !metadata.approvalLogic) return false;
    if (isChange && !metadata.changeMode) return false; // Enforce Change Mode
    if (accessErrors.length > 0) return false;
    return true;
  };

  const addField = () => {
    if (isLocked) return;
    const newField: FormField = {
      id: `field_${Date.now()}`,
      name: '',
      type: 'short-text',
      required: false,
      order: fields.length,
    };
    setEditingField(newField);
    setShowFieldDialog(true);
  };

  const editField = (field: FormField) => {
    if (isLocked) {
        toast.info("Fields are locked. Request a change to edit.");
        return;
    }
    // Prevent editing critical props of system fields? Maybe allow label change but not type.
    // For now, allow edit but warn if system.
    setEditingField(field);
    setShowFieldDialog(true);
  };

  const saveField = (saved: FormField) => {
    if (isLocked) return;
    const existingIndex = fields.findIndex(f => f.id === saved.id);
    if (existingIndex >= 0) {
      const updated = [...fields];
      updated[existingIndex] = saved;
      setFields(updated);
    } else {
      setFields([...fields, saved]);
    }
    setShowFieldDialog(false);
    setEditingField(null);
    toast.success('Field saved');
    if (step === 2) {
      const newProgress = calculateProgress();
      saveDraft(newProgress);
    }
  };

  const deleteField = (fieldId: string) => {
    if (isLocked) return;
    const field = fields.find(f => f.id === fieldId);
    if (field?.isSystem) {
        toast.error("Cannot delete mandatory system field.");
        return;
    }
    setFields(fields.filter(f => f.id !== fieldId));
    toast.success('Field deleted');
    if (step === 2) {
      const newProgress = Math.min(10 + ((fields.length - 1) * 10), 100);
      saveDraft(newProgress);
    }
  };

  const moveField = (dragIndex: number, hoverIndex: number) => {
    if (isLocked) return;
    const draggedField = fields[dragIndex];
    const updated = [...fields];
    updated.splice(dragIndex, 1);
    updated.splice(hoverIndex, 0, draggedField);
    updated.forEach((f, idx) => f.order = idx);
    setFields(updated);
  };

  const preparePayload = (completenessPercentage: number) => {
      return {
          companyId,
          sowId,
          workTypeId: serviceProjectId, // Explicit mapping (Phase 6.5)
          requestTypeName: metadata.title,
          description: metadata.description,
          assignmentGroup: metadata.assignmentGroup,
          assignmentLogic: metadata.assignmentLogic,
          approvalRequired: metadata.approvalNeeded,
          approvalLogic: metadata.approvalLogic,
          portalGroup: metadata.portalGroup,
          clientRequirementsNotes: metadata.clientRequirementsNotes,
          internalConsultantNotes: metadata.internalConsultantNotes,
          // Access Control (Phase 6.5)
          submitterAccess: metadata.submitterAccess,
          viewerAccess: metadata.viewerAccess,
          allowedTeams: parseNameList(metadata.allowedTeams),
          allowedGroups: parseNameList(metadata.allowedGroups),
          allowedDepartment: metadata.allowedDepartment?.trim() || undefined,
          allowedIndividuals: parseNameList(metadata.allowedIndividuals),
          allowedOrgs: parseNameList(metadata.allowedOrgs),
          externalAccessAllowed: metadata.externalAccessAllowed,
          team: metadata.team?.trim() || undefined,
          workTypeKey: metadata.workTypeKey || undefined,
          workTypeName: metadata.workTypeName || undefined,
          // Incident specific
          allowMajorIncident: metadata.allowMajorIncident,
          // Change specific
          changeMode: metadata.changeMode,
          
          fields: fields.map((f, idx) => toSavedField(f, idx)),
          userRole,
          completenessPercentage
      };
  };

  const handleSave = async (saveAndAddAnother = false) => {
    const canEditLocked = userRole !== 'client';
    if (isLocked && !canEditLocked) {
        toast.error("Cannot save changes: Request Type is LOCKED.");
        return;
    }
    if (!metadata.title?.trim()) {
      toast.error('Request Type Name is required');
      return;
    }
    if (isChange && !metadata.changeMode) {
        toast.error('Change Mode is required');
        return;
    }
    if (fields.length === 0) {
      toast.error('At least one field is required');
      return;
    }
    if (accessErrors.length > 0) {
      toast.error(accessErrors[0]);
      return;
    }
    
    const payload = preparePayload(100);
    
    try {
      // The record lives on the server through the draft save; write it before the caller closes the builder.
      if (!isLocked) await saveDraft(calculateProgress());
      if (saveAndAddAnother && onSaveAndAddAnother) {
        await onSaveAndAddAnother(payload);
      } else {
        await onSave(payload);
      }
    } catch (error) {
      toast.error('Failed to save request type');
    }
  };

  const handleProceedToStep2 = async () => {
    if (!canProceedToStep2()) return;
    if (!isLocked) await saveDraft(10);
    setStep(2);
  };

  const handleBackToStep1 = async () => {
    const progress = calculateProgress();
    if (!isLocked) await saveDraft(progress);
    setStep(1);
  };

  const calculateProgress = () => {
    return Math.min(10 + (fields.length * 10), 100);
  };

  const saveDraft = async (completenessPercentage: number = 100) => {
    if (isLocked) return; 
    if (!metadata.title && fields.length === 0) return;
    setIsSavingDraft(true);
    
    const draftData = preparePayload(completenessPercentage);
    // Draft payload needs serviceProjectId key specifically if backend expects it
    const payload = {
        ...draftData,
        serviceProjectId
    };

    try {
      const url = draftId || requestType?.id
        ? `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/request-type/${draftId || requestType?.id}`
        : `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/service-project/${serviceProjectId}/request-types`;
      const response = await fetch(url, {
        method: (draftId || requestType?.id) ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'x-user-role': userRole,
        },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        const data = await response.json();
        const savedId = data.requestType?.id || data.id;
        if (!draftId && !requestType?.id) {
          setDraftId(savedId);
        }
        if (onDraftSaved) {
          onDraftSaved(savedId);
        }
      } else {
        if (response.status === 409) {
            setIsLocked(true); 
            toast.error("Auto-save failed: Record is locked.");
        } else if (response.status === 400) {
            const err = await response.json().catch(() => ({}));
            toast.error(err.error || 'The form could not be saved');
        }
      }
    } catch (error) {
      console.error('Error saving draft:', error);
    } finally {
      setIsSavingDraft(false);
    }
  };

  useEffect(() => {
    if (isLocked) return;
    const timer = setTimeout(() => {
      if (step === 2 && (metadata.title || fields.length > 0)) {
        saveDraft(100);
      }
    }, 5000);
    return () => clearTimeout(timer);
  }, [step, metadata, fields, isLocked]);

  const executeTransition = async (action: string, extraData: any = {}) => {
    if (!draftId && !requestType?.id) {
        toast.error("Please save the request type first.");
        return;
    }
    const id = draftId || requestType?.id;
    const toastId = toast.loading("Updating status...");
    try {
        const response = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/request-type/${id}/transition`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ 
                action, 
                userRole,
                ...extraData
            }),
        });

        if (response.ok) {
            const data = await response.json();
            setStatus(data.requestType.status);
            setIsLocked(data.requestType.locked);
            toast.success("Status updated successfully", { id: toastId });
            setShowReadyForBuildDialog(false);
            setShowUnlockDialog(false);
        } else {
            const err = await response.json();
            // Handle Open Defects Blocking Approval
            if (response.status === 409 && err.code === 'OPEN_DEFECTS') {
                toast.error(`Blocked: ${err.openDefectCount} open defects must be resolved first.`, { id: toastId });
            } else {
                toast.error(err.error || "Failed to update status", { id: toastId });
            }
        }
    } catch (error) {
        toast.error("Network error", { id: toastId });
    }
  };

  const handleSubmitChangeRequest = async (data: any) => {
    const id = draftId || requestType?.id;
    if (!id) return;
    const toastId = toast.loading("Submitting request...");
    try {
        const response = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/request-type/${id}/change-request`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ 
                ...data,
                userRole 
            }),
        });
        if (response.ok) {
            toast.success("Submitted successfully", { id: toastId });
            setShowChangeRequestDialog(false);
        } else {
            toast.error("Failed to submit request", { id: toastId });
        }
    } catch (error) {
        toast.error("Network error", { id: toastId });
    }
  };

  const getStatusBadge = () => {
    const config: Record<string, { color: string, label: string }> = {
        draft: { color: "bg-gray-100 text-gray-800", label: "Draft" },
        awaiting_client: { color: "bg-orange-100 text-orange-800", label: "Awaiting Client" },
        ready_for_build: { color: "bg-blue-100 text-blue-800", label: "Ready for Build" },
        in_build: { color: "bg-purple-100 text-purple-800", label: "In Build" },
        ready_for_test: { color: "bg-cyan-100 text-cyan-800", label: "Ready for Test" },
        in_test: { color: "bg-amber-100 text-amber-800", label: "In Testing" },
        approved: { color: "bg-green-100 text-green-800", label: "Approved" },
        blocked: { color: "bg-red-100 text-red-800", label: "Blocked" },
        cancelled: { color: "bg-gray-200 text-gray-600", label: "Canceled" },
        draft_revision: { color: "bg-purple-50 text-purple-700 border-purple-200", label: "Revision in Progress" },
    };
    return config[status] || config['draft'];
  };

  const canMarkReadyForBuild = calculateProgress() >= 90 && fields.length > 0 && !isLocked;

  const renderPrimaryAction = () => {
    if (isLocked) {
        if (status === 'ready_for_build' && userRole !== 'client') {
            return (
                <Button onClick={() => executeTransition('start_build')} className="w-full sm:w-auto">
                    Start Build <Play className="w-4 h-4 ml-2" />
                </Button>
            );
        }
        if (status === 'in_build' && userRole !== 'client') {
             return (
                <Button onClick={() => executeTransition('mark_ready_for_test')} className="w-full sm:w-auto">
                    Mark Ready for Test <CheckCircle className="w-4 h-4 ml-2" />
                </Button>
            );
        }
        if ((status === 'ready_for_test' || status === 'in_test') && userRole === 'client') {
             return (
                <Button className="bg-green-600 hover:bg-green-700 w-full sm:w-auto" onClick={() => executeTransition('approve')}>
                    Approve <CheckCircle className="w-4 h-4 ml-2" />
                </Button>
            );
        }
        return null;
    } else {
        return (
            <Button 
                onClick={() => setShowReadyForBuildDialog(true)}
                disabled={!canMarkReadyForBuild}
                className={`w-full sm:w-auto ${canMarkReadyForBuild ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
            >
                {!canMarkReadyForBuild ? `Complete Requirements (${calculateProgress()}%)` : 'Ready for Build'}
                <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
        );
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-50">
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        
        {/* Status Banner */}
        <Card className={`border-l-4 ${isLocked ? 'border-l-blue-500 bg-blue-50/50' : 'border-l-slate-400 bg-white'}`}>
            <CardContent className="pt-6 pb-6 flex items-center justify-between">
                <div>
                    <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-semibold text-lg flex items-center gap-2">
                            Status: <Badge className={getStatusBadge().color}>{getStatusBadge().label}</Badge>
                            {isLocked && <Lock className="w-4 h-4 text-muted-foreground" />}
                        </h3>
                    </div>
                    <p className="text-sm text-muted-foreground">
                        {status === 'draft' && "Draft mode. Define requirements freely."}
                        {status === 'ready_for_build' && "Requirements LOCKED. Consultant is notified to start build."}
                        {status === 'in_build' && "Consultant is actively building. Requirements are read-only."}
                        {status === 'ready_for_test' && "Build complete. Please verify and approve."}
                        {status === 'approved' && "Approved for production deployment."}
                        {status === 'draft_revision' && "Unlocked for revisions. Submit as 'Ready for Build' when done."}
                    </p>
                </div>
                <div className="flex gap-2">
                     {isLocked && (
                         <Button variant="outline" size="sm" onClick={() => setShowUnlockDialog(true)}>
                            {userRole !== 'client' ? 'Force Unlock' : 'Unlock / Edit'}
                            <Lock className="w-3 h-3 ml-2" />
                         </Button>
                     )}
                     {userRole === 'client' && (status === 'ready_for_test' || status === 'in_test') && (
                         <Button variant="outline" size="sm" onClick={() => setShowChangeRequestDialog(true)}>
                            Report Issue
                         </Button>
                     )}
                </div>
            </CardContent>
        </Card>

        {/* Inherited Config Banner */}
        {inheritedConfig && (
              <div className="bg-white border border-slate-200 rounded-lg p-4">
                 <div className="flex items-center gap-2 mb-3 text-slate-800 font-semibold border-b border-slate-200 pb-2">
                    <Shield className="h-4 w-4 text-blue-600" />
                    <span>Inherited Policies & Rules</span>
                    <Badge variant="outline" className="ml-auto bg-white text-xs font-normal text-slate-500">
                        Read Only
                    </Badge>
                 </div>
                 <p className="text-xs text-slate-600 mb-4">
                    This Request Type inherits global governance policies from the parent Work Type.
                 </p>
                 
                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Workflow */}
                    <div className="bg-slate-50 p-3 rounded border border-slate-100">
                        <div className="text-xs font-bold text-slate-700 mb-2 flex items-center gap-1">
                            <GitMerge className="h-3 w-3" /> Inherited Workflow
                        </div>
                        <div className="text-xs text-slate-600 font-medium">
                            {normalizeAcronyms(inheritedConfig.workflowConfig?.workflowTemplateId?.split('-').map((s: string) => s.charAt(0).toUpperCase() + s.slice(1)).join(' ') || '')}
                        </div>
                    </div>

                    {/* Change Specifics */}
                    {isChange && (
                        <>
                            <div className="bg-slate-50 p-3 rounded border border-slate-100">
                                <div className="text-xs font-bold text-slate-700 mb-2 flex items-center gap-1">
                                    <AlertTriangle className="h-3 w-3" /> Risk Model
                                </div>
                                <div className="text-xs text-slate-600 font-medium capitalize">
                                    {inheritedConfig.riskModel === 'calculator' ? 'Risk Calculator' : 'Manual Selection'}
                                </div>
                            </div>
                            <div className="bg-slate-50 p-3 rounded border border-slate-100">
                                <div className="text-xs font-bold text-slate-700 mb-2 flex items-center gap-1">
                                    <Calendar className="h-3 w-3" /> Blackouts
                                </div>
                                <div className="text-xs text-slate-600 font-medium">
                                    {inheritedConfig.blackouts ? 'Enforced' : 'Not Enforced'}
                                </div>
                            </div>
                        </>
                    )}

                    {/* SLA Targets (Incident/SRM) */}
                    {!isChange && (
                        <div className="bg-slate-50 p-3 rounded border border-slate-100">
                            <div className="text-xs font-bold text-slate-700 mb-2 flex items-center gap-1">
                                <Info className="h-3 w-3" /> SLA Targets
                            </div>
                            <div className="space-y-1">
                                {['Critical', 'High'].map(prio => (
                                    <div key={prio} className="flex justify-between text-xs">
                                        <span className={prio === 'Critical' ? 'text-red-600 font-medium' : 'text-orange-600 font-medium'}>
                                            {prio}
                                        </span>
                                        <span className="text-slate-500 font-mono">
                                            {inheritedConfig.sla?.response?.[prio]}h / {inheritedConfig.sla?.resolution?.[prio]}h
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                 </div>
              </div>
        )}

        {/* Step Wizard Nav */}
        <div className="flex items-center gap-4">
            <div 
                className={`flex items-center gap-2 cursor-pointer ${step === 1 ? 'text-blue-600' : 'text-gray-500'}`}
                onClick={() => setStep(1)}
            >
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                1
            </div>
            <span className="font-medium">Form Details</span>
            </div>
            <div className="flex-1 h-0.5 bg-gray-200">
            <div className={`h-full bg-blue-600 transition-all ${step === 2 ? 'w-full' : 'w-0'}`} />
            </div>
            <div 
                className={`flex items-center gap-2 cursor-pointer ${step === 2 ? 'text-blue-600' : 'text-gray-500'}`}
                onClick={() => canProceedToStep2() && setStep(2)}
            >
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                2
            </div>
            <span className="font-medium">Build Form</span>
            </div>
        </div>

        {/* STEP 1 CONTENT */}
        {step === 1 && (
            <Card>
            <CardHeader>
                <CardTitle>Request Type Details</CardTitle>
                <CardDescription>
                Configure the high-level settings for this service request type
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                <fieldset disabled={isLocked} className="space-y-6 disabled:opacity-80">
                    <div className="space-y-2">
                    <Label htmlFor="title">Form Title/Name <span className="text-red-500">*</span></Label>
                    <Input
                        id="title"
                        value={metadata.title}
                        onChange={(e) => handleMetadataChange('title', e.target.value)}
                        placeholder="e.g., Hardware Request, Software Access"
                    />
                    </div>
                    <div className="space-y-2">
                    <Label htmlFor="description">Form Description</Label>
                    <Textarea
                        id="description"
                        value={metadata.description || ''}
                        onChange={(e) => handleMetadataChange('description', e.target.value)}
                        placeholder="Optional: Only if the title needs clarification"
                        rows={2}
                    />
                    </div>
                    
                    {/* Phase 6.5: Access Control */}
                    <div className="space-y-4 p-4 bg-slate-50 border rounded-lg">
                        <h3 className="font-medium flex items-center gap-2">
                            <Users className="h-4 w-4 text-slate-500" />
                            Access Control
                        </h3>
                        
                        <div className="grid grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <Label>Who can submit?</Label>
                                <Select 
                                    value={metadata.submitterAccess} 
                                    onValueChange={(val) => handleMetadataChange('submitterAccess', val)}
                                >
                                    <SelectTrigger aria-label="Who can submit">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {SUBMITTER_ACCESS_OPTIONS.map((o) => (
                                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Visibility</Label>
                                <Select 
                                    value={metadata.viewerAccess} 
                                    onValueChange={(val) => handleMetadataChange('viewerAccess', val)}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="requester_only">Requester Only</SelectItem>
                                        <SelectItem value="organization">Their Organization</SelectItem>
                                        <SelectItem value="groups">Specific Groups</SelectItem>
                                        <SelectItem value="agents_only">Agents Only (Internal)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {metadata.submitterAccess === 'specific_teams' && (
                            <div className="space-y-3 rounded-md border bg-white p-3" data-testid="allowed-teams">
                                <Label>Teams that can submit</Label>
                                {allTeams.length === 0 && <p className="text-xs text-muted-foreground">No teams yet. Add one below; it is saved on the company's space.</p>}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {allTeams.map((team) => (
                                        <label key={team} className="flex items-center gap-2 text-sm cursor-pointer">
                                            <Checkbox checked={metadata.allowedTeams.includes(team)} onCheckedChange={() => toggleAllowedTeam(team)} aria-label={`Team ${team}`} />
                                            <span>{team}</span>
                                        </label>
                                    ))}
                                </div>
                                <div className="flex gap-2">
                                    <Input
                                        value={newTeamName}
                                        onChange={(e) => setNewTeamName(e.target.value)}
                                        placeholder="New team name"
                                        aria-label="New team name"
                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void handleAddTeam('allowed'); } }}
                                    />
                                    <Button type="button" variant="outline" size="sm" onClick={() => handleAddTeam('allowed')} disabled={!newTeamName.trim() || addingTeam}>
                                        <Plus className="h-4 w-4 mr-1" /> Add team
                                    </Button>
                                </div>
                            </div>
                        )}

                        {metadata.submitterAccess === 'specific_department' && (
                            <div className="space-y-2">
                                <Label htmlFor="allowedDepartment">Department</Label>
                                <Input
                                    id="allowedDepartment"
                                    value={metadata.allowedDepartment || ''}
                                    onChange={(e) => handleMetadataChange('allowedDepartment', e.target.value)}
                                    placeholder="e.g. Finance"
                                />
                            </div>
                        )}

                        {metadata.submitterAccess === 'specific_individuals' && (
                            <div className="space-y-2">
                                <Label htmlFor="allowedIndividuals">Individuals (one per line, or comma-separated)</Label>
                                <Textarea
                                    id="allowedIndividuals"
                                    value={metadata.allowedIndividuals || ''}
                                    onChange={(e) => handleMetadataChange('allowedIndividuals', e.target.value)}
                                    placeholder="Jane Doe\njohn@example.com"
                                    rows={3}
                                />
                            </div>
                        )}

                        {metadata.submitterAccess === 'specific_groups' && (
                            <div className="space-y-2">
                                <Label>Allowed Groups (comma separated)</Label>
                                <Input 
                                    value={metadata.allowedGroups || ''}
                                    onChange={(e) => handleMetadataChange('allowedGroups', e.target.value)}
                                    placeholder="e.g. jira-users, hr-team"
                                />
                            </div>
                        )}

                        {metadata.submitterAccess === 'specific_orgs' && (
                            <div className="space-y-2">
                                <Label>Allowed Organizations (comma separated)</Label>
                                <Input 
                                    value={metadata.allowedOrgs || ''}
                                    onChange={(e) => handleMetadataChange('allowedOrgs', e.target.value)}
                                    placeholder="e.g. Acme Corp, Contoso"
                                />
                            </div>
                        )}

                        <div className="flex items-center gap-2 pt-2">
                            <Switch 
                                id="external"
                                checked={metadata.externalAccessAllowed}
                                onCheckedChange={(c) => handleMetadataChange('externalAccessAllowed', c)}
                            />
                            <Label htmlFor="external" className="flex items-center gap-2 cursor-pointer">
                                <Globe className="h-3 w-3" />
                                Allow External Access (Portal)
                            </Label>
                        </div>
                        {accessErrors.length > 0 && (
                            <p className="text-xs text-red-600" role="alert" data-testid="access-errors">{accessErrors[0]}</p>
                        )}
                    </div>

                    {/* Team and work type (RESTORE_PLAN R1.3) */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-slate-50 border rounded-lg">
                        <div className="space-y-2">
                            <Label>Owning team</Label>
                            <Select value={metadata.team || '__none__'} onValueChange={(v) => handleMetadataChange('team', v === '__none__' ? '' : v)}>
                                <SelectTrigger aria-label="Owning team"><SelectValue placeholder="No team" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__none__">No team</SelectItem>
                                    {allTeams.map((team) => <SelectItem key={team} value={team}>{team}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground">The forms list groups request types under their team.</p>
                            {metadata.submitterAccess !== 'specific_teams' && (
                                <div className="flex gap-2">
                                    <Input
                                        value={newTeamName}
                                        onChange={(e) => setNewTeamName(e.target.value)}
                                        placeholder="New team name"
                                        aria-label="New owning team name"
                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void handleAddTeam('owner'); } }}
                                    />
                                    <Button type="button" variant="outline" size="sm" onClick={() => handleAddTeam('owner')} disabled={!newTeamName.trim() || addingTeam}>
                                        <Plus className="h-4 w-4 mr-1" /> Add team
                                    </Button>
                                </div>
                            )}
                        </div>
                        <div className="space-y-2">
                            <Label>Work type</Label>
                            <Select
                                value={metadata.workTypeKey || '__none__'}
                                onValueChange={(v) => {
                                    const wt = workTypeChoices.find((w) => w.id === v);
                                    setMetadata((prev) => ({ ...prev, workTypeKey: wt?.id || '', workTypeName: wt?.name || '' }));
                                }}
                            >
                                <SelectTrigger aria-label="Work type"><SelectValue placeholder="Choose a work type" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__none__">Not set</SelectItem>
                                    {workTypeChoices.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground">Each request type maps to one work type; its workflow and SLA come from there.</p>
                        </div>
                    </div>

                    {isIncident && (
                        <div className="space-y-2 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                            <Label htmlFor="allowMajorIncident" className="flex items-center gap-2 text-orange-800">
                                <Flame className="h-4 w-4" /> Major Incident Escalation
                            </Label>
                            <div className="flex items-center gap-2 mt-2">
                                <Switch 
                                    id="allowMajorIncident"
                                    checked={metadata.allowMajorIncident || false}
                                    onCheckedChange={(c) => handleMetadataChange('allowMajorIncident', c)}
                                    disabled={isLocked}
                                />
                                <span className="text-sm font-medium">{metadata.allowMajorIncident ? 'Allowed' : 'Disabled'}</span>
                            </div>
                            <p className="text-xs text-orange-700 mt-1">
                                If enabled, users can flag this as a major incident, triggering specialized workflows.
                            </p>
                        </div>
                    )}

                    {isChange && (
                        <div className="space-y-2 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <Label htmlFor="changeMode" className="flex items-center gap-2 text-blue-800">
                                <GitMerge className="h-4 w-4" /> Change Mode <span className="text-red-500">*</span>
                            </Label>
                            <p className="text-xs text-blue-700 mb-2">
                                Which operational mode does this Request Type belong to?
                            </p>
                            <Select 
                                value={metadata.changeMode} 
                                onValueChange={(val) => handleMetadataChange('changeMode', val)}
                            >
                                <SelectTrigger className="bg-white">
                                    <SelectValue placeholder="Select Change Mode..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {(inheritedConfig?.workflowConfig?.changeTypes || []).map((type: string) => (
                                        <SelectItem key={type} value={type} className="capitalize">
                                            {type} Change
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {(inheritedConfig?.workflowConfig?.changeTypes || []).length === 0 && (
                                <p className="text-xs text-red-600 mt-1">
                                    Warning: No Change Modes are enabled in Global Configuration. Go back and enable at least one.
                                </p>
                            )}
                        </div>
                    )}

                    <div className="space-y-2">
                    <Label htmlFor="assignmentGroup">Assignment Group (Queue)</Label>
                    <Input
                        id="assignmentGroup"
                        value={metadata.assignmentGroup || ''}
                        onChange={(e) => handleMetadataChange('assignmentGroup', e.target.value)}
                        placeholder="e.g., IT Hardware Team, Software Access Team"
                    />
                    </div>
                    {metadata.assignmentGroup && (
                    <div className="space-y-2">
                        <Label htmlFor="assignmentLogic">Assignment Logic <span className="text-red-500">*</span></Label>
                        <Textarea
                        id="assignmentLogic"
                        value={metadata.assignmentLogic || ''}
                        onChange={(e) => handleMetadataChange('assignmentLogic', e.target.value)}
                        placeholder="e.g., Route to IT Hardware if request type = 'Hardware'"
                        rows={3}
                        />
                    </div>
                    )}
                    <div className="space-y-2">
                    <Label htmlFor="approvalNeeded">Might this form require approval(s)? <span className="text-red-500">*</span></Label>
                    <div className="flex items-center gap-2">
                        <Switch
                        id="approvalNeeded"
                        checked={metadata.approvalNeeded}
                        onCheckedChange={(checked) => handleMetadataChange('approvalNeeded', checked)}
                        disabled={isLocked}
                        />
                        <span className="text-sm">{metadata.approvalNeeded ? 'Yes' : 'No'}</span>
                    </div>
                    </div>
                    {metadata.approvalNeeded && (
                    <div className="space-y-2">
                        <Label htmlFor="approvalLogic">Approval Logic <span className="text-red-500">*</span></Label>
                        <Textarea
                        id="approvalLogic"
                        value={metadata.approvalLogic || ''}
                        onChange={(e) => handleMetadataChange('approvalLogic', e.target.value)}
                        placeholder="e.g., If cost >= $1000, require manager approval."
                        rows={3}
                        />
                    </div>
                    )}
                    <div className="space-y-2">
                    <Label htmlFor="portalGroup">Portal Group</Label>
                    <Input
                        id="portalGroup"
                        value={metadata.portalGroup || ''}
                        onChange={(e) => handleMetadataChange('portalGroup', e.target.value)}
                        placeholder="e.g., Hardware & Equipment, Software & Licenses"
                    />
                    </div>
                    <div className="space-y-2">
                    <Label htmlFor="notes">Client Requirement Notes</Label>
                    <Textarea
                        id="notes"
                        value={metadata.clientRequirementsNotes || ''}
                        onChange={(e) => handleMetadataChange('clientRequirementsNotes', e.target.value)}
                        placeholder="Any additional notes or context (Locked after build starts)"
                        rows={3}
                    />
                    </div>
                </fieldset>
                {userRole !== 'client' && (
                    <div className="space-y-2 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                        <Label htmlFor="internalConsultantNotes" className="flex items-center gap-2">
                            <Lock className="w-3 h-3 text-yellow-600" />
                            InternalConsultant Notes (Private)
                        </Label>
                        <Textarea
                            id="internalConsultantNotes"
                            value={metadata.internalConsultantNotes || ''}
                            onChange={(e) => handleMetadataChange('internalConsultantNotes', e.target.value)}
                            placeholder="Internal implementation notes, technical details, etc. (Always editable)"
                            rows={3}
                        />
                        <div className="text-xs text-yellow-700">
                            These notes are visible only to consultants and can be edited even when the request type is locked.
                        </div>
                    </div>
                )}
            </CardContent>
            </Card>
        )}

        {/* STEP 2 CONTENT */}
        {step === 2 && (
            <DndProvider backend={HTML5Backend}>
            <div className="space-y-4">
                <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                    <div>
                        <CardTitle>Form Builder: {metadata.title}</CardTitle>
                        <CardDescription>Add and arrange fields for this request type</CardDescription>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => setShowPreview(true)}><Eye className="w-4 h-4 mr-2" />Preview</Button>
                        {!isLocked && (
                            <Button onClick={addField}><Plus className="w-4 h-4 mr-2" />Add Field</Button>
                        )}
                    </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {fields.length === 0 ? (
                    <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
                        <p className="text-gray-500 mb-4">No fields added yet</p>
                        {!isLocked && <Button onClick={addField}><Plus className="w-4 h-4 mr-2" />Add Your First Field</Button>}
                    </div>
                    ) : (
                    <div className="space-y-2">
                        {fields.sort((a, b) => a.order - b.order).map((field, index) => (
                            <RequestTypeFieldCard
                                key={field.id}
                                field={field}
                                index={index}
                                allFields={fields}
                                locked={isLocked}
                                onEdit={() => editField(field)}
                                onDelete={() => deleteField(field.id)}
                                onMove={moveField}
                            />
                        ))}
                    </div>
                    )}
                </CardContent>
                </Card>
            </div>
            </DndProvider>
        )}
      </div>

      {/* Persistent Footer */}
      <div className="p-4 bg-white border-t flex items-center justify-between sticky bottom-0 z-10 shadow-[0_-2px_10px_rgba(0,0,0,0.05)]">
        <div className="flex gap-2">
            <Button variant="ghost" onClick={onCancel}>Close</Button>
            {/* Navigation Buttons for Wizard */}
            {step === 2 && !isLocked && (
                <Button variant="outline" onClick={handleBackToStep1}>Back to Details</Button>
            )}
            {step === 1 && !isLocked && (
                <Button variant="outline" onClick={handleProceedToStep2} disabled={!canProceedToStep2()}>Next Step</Button>
            )}
        </div>
        
        <div className="flex gap-2 items-center">
             {!isLocked && (
                 <Button variant="ghost" onClick={() => handleSave(false)}>
                     Save Draft
                 </Button>
             )}
             
             {/* PRIMARY ACTION */}
             {renderPrimaryAction()}
        </div>
      </div>

      <ReadyForBuildDialog
         open={showReadyForBuildDialog}
         onClose={() => setShowReadyForBuildDialog(false)}
         onConfirm={() => executeTransition('mark_ready_for_build')}
      />

      <RequestTypeFieldEditor
        open={showFieldDialog}
        field={editingField}
        allFields={fields}
        onClose={() => { setShowFieldDialog(false); setEditingField(null); }}
        onSave={saveField}
      />

      <PreviewDialog
        open={showPreview}
        metadata={metadata}
        fields={fields}
        onClose={() => setShowPreview(false)}
      />

      <ChangeRequestDialog 
        open={showChangeRequestDialog}
        onClose={() => setShowChangeRequestDialog(false)}
        onSubmit={handleSubmitChangeRequest}
      />
      
      <UnlockDialog
        open={showUnlockDialog}
        onClose={() => setShowUnlockDialog(false)}
        onUnlock={(data) => executeTransition('unlock', data)}
      />
    </div>
  );
}

// --- DIALOGS & COMPONENTS ---

function PreviewDialog({ open, metadata, fields, onClose }: any) {
    const ordered = [...fields].sort((a: any, b: any) => a.order - b.order);
    const shown = ordered.filter((f: any) => !f.hideAndPrepopulate);
    const hidden = ordered.filter((f: any) => f.hideAndPrepopulate);
    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Preview: {metadata.title || 'Untitled Form'}</DialogTitle>
                    <DialogDescription>{metadata.description}</DialogDescription>
                </DialogHeader>
                <div className="space-y-6 py-4">
                    {shown.map((field: any) => {
                        const condition = describeCondition(field, fields);
                        return (
                        <div key={field.id} className="space-y-2">
                            <Label>
                                {field.portalLabel || field.name}
                                {field.required && <span className="text-red-500 ml-1">*</span>}
                            </Label>
                            {field.helpText && <p className="text-xs text-muted-foreground">{field.helpText}</p>}
                            
                            {['short-text', 'email', 'url', 'number', 'user-single', 'group-single'].includes(field.type) && <Input disabled placeholder={field.defaultValue || 'Text input'} />}
                            {['paragraph'].includes(field.type) && <Textarea disabled placeholder={field.defaultValue || 'Long text'} />}
                            {['dropdown', 'radio'].includes(field.type) && (
                                <Select disabled>
                                    <SelectTrigger><SelectValue placeholder={field.defaultValue || 'Select...'} /></SelectTrigger>
                                </Select>
                            )}
                            {field.type === 'cascading' && (
                                <div className="grid grid-cols-2 gap-2">
                                    <Select disabled><SelectTrigger><SelectValue placeholder="Parent..." /></SelectTrigger></Select>
                                    <Select disabled><SelectTrigger><SelectValue placeholder="Child..." /></SelectTrigger></Select>
                                </div>
                            )}
                            {['checkboxes', 'multiselect', 'user-multiple', 'group-multiple'].includes(field.type) && (
                                <div className="border rounded p-2 bg-gray-50 text-sm text-gray-400">
                                    {(field.options || []).length > 0 ? field.options.join(' · ') : 'Multi-select options...'}
                                </div>
                            )}
                            {field.type === 'asset' && (
                                <div className="border rounded p-2 bg-gray-50 text-sm text-gray-400">Assets object picker{(field.options || []).length > 0 ? `: ${field.options.join(', ')}` : ''}</div>
                            )}
                            {['date', 'datetime', 'time'].includes(field.type) && <Input disabled type={field.type === 'time' ? 'time' : 'date'} />}
                            {['attachment'].includes(field.type) && <div className="border-2 border-dashed p-4 text-center text-gray-400 rounded">Drop files here</div>}
                            {condition && <p className="text-xs text-muted-foreground italic">{condition}</p>}
                        </div>
                        );
                    })}
                    {hidden.length > 0 && (
                        <div className="rounded border border-dashed p-3 text-xs text-muted-foreground">
                            Hidden from the portal, filled automatically: {hidden.map((f: any) => `${f.name} = "${f.prepopulatedValue || ''}"`).join('; ')}
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button onClick={onClose}>Close Preview</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ReadyForBuildDialog({ open, onClose, onConfirm }: { open: boolean, onClose: () => void, onConfirm: () => void }) {
    const [agreed, setAgreed] = useState(false);
    const [name, setName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if(open) {
            setAgreed(false);
            setName('');
            setIsSubmitting(false);
        }
    }, [open]);

    const handleConfirm = async () => {
        setIsSubmitting(true);
        await onConfirm();
        setIsSubmitting(false);
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <AlertOctagon className="h-5 w-5 text-blue-600" />
                        Confirm Implementation Readiness
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                    <Alert className="bg-blue-50 border-blue-200">
                        <Info className="h-4 w-4 text-blue-600" />
                        <AlertDescription className="text-blue-800 text-sm">
                            "By marking this Specification as Ready for Build, you confirm it meets all gathered requirements. Changes after this point cause re-work and may require a Change Order."
                        </AlertDescription>
                    </Alert>

                    <div className="flex items-start gap-2 p-3 border rounded-md">
                         <Checkbox 
                            id="agree" 
                            checked={agreed}
                            onCheckedChange={(c) => setAgreed(c === true)}
                         />
                         <Label htmlFor="agree" className="text-sm font-medium cursor-pointer leading-tight">
                             I understand and agree that this locks the specification.
                         </Label>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="signerName">Type your full name to confirm</Label>
                        <Input 
                            id="signerName"
                            placeholder="e.g. Jane Doe" 
                            value={name} 
                            onChange={(e) => setName(e.target.value)}
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button 
                        onClick={handleConfirm}
                        disabled={!agreed || !name.trim() || isSubmitting}
                        className="bg-blue-600 hover:bg-blue-700"
                    >
                        {isSubmitting ? 'Processing...' : 'Ready for Build'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function UnlockDialog({ open, onClose, onUnlock }: { open: boolean, onClose: () => void, onUnlock: (data: any) => void }) {
    const [reasonType, setReasonType] = useState<'defect' | 'scope_change'>('defect');
    const [reason, setReason] = useState('');

    useEffect(() => {
        if(open) {
            setReasonType('defect');
            setReason('');
        }
    }, [open]);

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>Reason for Modification</DialogTitle>
                    <DialogDescription>
                         Unlocking will revert the status to allow editing. Please classify the reason.
                    </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-6 py-2">
                    <RadioGroup value={reasonType} onValueChange={(v) => setReasonType(v as any)} className="grid grid-cols-2 gap-4">
                        <div className={`flex flex-col items-center justify-between rounded-md border-2 p-4 hover:bg-slate-50 cursor-pointer ${reasonType === 'defect' ? 'border-primary bg-slate-50' : 'border-muted'}`}>
                            <RadioGroupItem value="defect" id="unlock-defect" className="sr-only" />
                            <Label htmlFor="unlock-defect" className="cursor-pointer text-center w-full">
                                <Bug className="w-8 h-8 mx-auto mb-2 text-red-500" />
                                <div className="font-semibold">Defect</div>
                                <div className="text-xs text-muted-foreground mt-1">
                                    Correction needed to meet approved baseline
                                </div>
                            </Label>
                        </div>
                        <div className={`flex flex-col items-center justify-between rounded-md border-2 p-4 hover:bg-slate-50 cursor-pointer ${reasonType === 'scope_change' ? 'border-primary bg-slate-50' : 'border-muted'}`}>
                            <RadioGroupItem value="scope_change" id="unlock-scope" className="sr-only" />
                            <Label htmlFor="unlock-scope" className="cursor-pointer text-center w-full">
                                <FileText className="w-8 h-8 mx-auto mb-2 text-blue-500" />
                                <div className="font-semibold">Scope Change</div>
                                <div className="text-xs text-muted-foreground mt-1">
                                    New or changed requirements
                                </div>
                            </Label>
                        </div>
                    </RadioGroup>

                    {reasonType === 'scope_change' && (
                         <Alert variant="default" className="bg-yellow-50 border-yellow-200">
                             <AlertTriangle className="h-4 w-4 text-yellow-600" />
                             <AlertDescription className="text-yellow-800 text-xs">
                                 This will flag the record as a Change Order candidate.
                             </AlertDescription>
                         </Alert>
                    )}

                    <div className="space-y-2">
                         <Label>Description <span className="text-red-500">*</span></Label>
                         <Textarea 
                             placeholder="Provide details about the change..." 
                             value={reason}
                             onChange={(e) => setReason(e.target.value)}
                         />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button 
                        onClick={() => {
                            if (!reason.trim()) return toast.error("Description is required");
                            onUnlock({ reasonType, reason });
                        }}
                    >
                        Unlock & Edit
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ChangeRequestDialog({ open, onClose, onSubmit }: { open: boolean, onClose: () => void, onSubmit: (data: any) => void }) {
    const [type, setType] = useState('defect');
    const [description, setDescription] = useState('');
    const [impactedAreas, setImpactedAreas] = useState('');
    const [severity, setSeverity] = useState('medium');
    const [expectedBehavior, setExpectedBehavior] = useState('');
    const [actualBehavior, setActualBehavior] = useState('');
    const [showScopeWarning, setShowScopeWarning] = useState(false);

    useEffect(() => {
        if (open) {
            setType('defect');
            setDescription('');
            setImpactedAreas('');
            setSeverity('medium');
            setExpectedBehavior('');
            setActualBehavior('');
            setShowScopeWarning(false);
        }
    }, [open]);

    useEffect(() => {
        if (type === 'defect') {
            const scopeKeywords = ['add', 'new', 'change', 'requirement', 'feature', 'integration', 'workflow', 'field'];
            const allText = `${description} ${expectedBehavior}`.toLowerCase();
            const hasKeywords = scopeKeywords.some(kw => allText.includes(kw));
            setShowScopeWarning(hasKeywords);
        } else {
            setShowScopeWarning(false);
        }
    }, [description, expectedBehavior, type]);

    const handleSubmit = () => {
        if (type === 'defect') {
            if (!expectedBehavior || !actualBehavior) return toast.error("Please describe Expected vs Actual behavior.");
            onSubmit({ 
                type, 
                description: `Defect: ${description}`, 
                severity, 
                expectedBehavior, 
                actualBehavior 
            });
        } else {
            if (!description) return toast.error("Description is required");
            onSubmit({ 
                type, 
                description, 
                impactedAreas: impactedAreas.split(',').map(s => s.trim()).filter(Boolean) 
            });
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-xl">
                <DialogHeader>
                    <DialogTitle>Submit Request</DialogTitle>
                    <DialogDescription>
                        Is this a defect (bug) or a change in requirements?
                    </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 py-2">
                    <RadioGroup value={type} onValueChange={setType} className="grid grid-cols-2 gap-4">
                        <div className={`flex flex-col items-center justify-between rounded-md border-2 p-4 hover:bg-slate-50 cursor-pointer ${type === 'defect' ? 'border-primary bg-slate-50' : 'border-muted'}`}>
                            <RadioGroupItem value="defect" id="defect" className="sr-only" />
                            <Label htmlFor="defect" className="cursor-pointer text-center w-full">
                                <Bug className="w-8 h-8 mx-auto mb-2 text-red-500" />
                                <div className="font-semibold">Defect / Fix</div>
                                <div className="text-xs text-muted-foreground mt-1">
                                    "It's broken or doesn't match what we agreed."
                                </div>
                            </Label>
                        </div>
                        <div className={`flex flex-col items-center justify-between rounded-md border-2 p-4 hover:bg-slate-50 cursor-pointer ${type === 'scope_change' ? 'border-primary bg-slate-50' : 'border-muted'}`}>
                            <RadioGroupItem value="scope_change" id="scope_change" className="sr-only" />
                            <Label htmlFor="scope_change" className="cursor-pointer text-center w-full">
                                <FileText className="w-8 h-8 mx-auto mb-2 text-blue-500" />
                                <div className="font-semibold">Scope Change</div>
                                <div className="text-xs text-muted-foreground mt-1">
                                    "I want to change or add requirements."
                                </div>
                            </Label>
                        </div>
                    </RadioGroup>

                    {type === 'defect' && (
                        <div className="space-y-4 animate-in fade-in slide-in-from-top-2">
                             {showScopeWarning && (
                                <Alert variant="warning" className="bg-yellow-50 border-yellow-200">
                                    <AlertTriangle className="h-4 w-4 text-yellow-600" />
                                    <AlertTitle className="text-yellow-800">Potential Scope Change Detected</AlertTitle>
                                    <AlertDescription className="text-yellow-700 text-xs">
                                        Your description sounds like a new requirement. If you are adding features, please select <strong>Scope Change</strong> to avoid delays. Defects are strictly for fixing broken functionality.
                                    </AlertDescription>
                                </Alert>
                            )}

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Expected Behavior</Label>
                                    <Textarea 
                                        placeholder="What should happen?" 
                                        value={expectedBehavior}
                                        onChange={e => setExpectedBehavior(e.target.value)}
                                        rows={3}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Actual Behavior</Label>
                                    <Textarea 
                                        placeholder="What is happening instead?" 
                                        value={actualBehavior}
                                        onChange={e => setActualBehavior(e.target.value)}
                                        rows={3}
                                    />
                                </div>
                            </div>
                            
                            <div className="space-y-2">
                                <Label>Additional Context</Label>
                                <Input 
                                    placeholder="Steps to reproduce, browser, etc." 
                                    value={description}
                                    onChange={e => setDescription(e.target.value)}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Severity</Label>
                                <Select value={severity} onValueChange={setSeverity}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="low">Low (Cosmetic)</SelectItem>
                                        <SelectItem value="medium">Medium (Functional)</SelectItem>
                                        <SelectItem value="high">High (Blocker)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    )}

                    {type === 'scope_change' && (
                         <div className="space-y-4 animate-in fade-in slide-in-from-top-2">
                            <Alert className="bg-blue-50 border-blue-200">
                                <Info className="h-4 w-4 text-blue-600" />
                                <AlertDescription className="text-blue-700 text-xs">
                                    Submitting a Scope Change may require a Change Order and could impact timeline/budget.
                                </AlertDescription>
                            </Alert>

                            <div className="space-y-2">
                                <Label>Description of Change</Label>
                                <Textarea 
                                    placeholder="What do you want to change and why?" 
                                    value={description}
                                    onChange={e => setDescription(e.target.value)}
                                    rows={4}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Impacted Areas</Label>
                                <Input 
                                    placeholder="e.g. Due Date field, Approval logic" 
                                    value={impactedAreas}
                                    onChange={e => setImpactedAreas(e.target.value)}
                                />
                            </div>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={handleSubmit}>Submit</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
